import { lazy, Suspense, useEffect } from 'react'

import { createRootRouteWithContext, HeadContent, Outlet, retainSearchParams } from '@tanstack/react-router'
import z from 'zod'

const SofaDevtools = lazy(() => import('@/features/sofa-devtools'))

import type { QueryClient } from '@tanstack/react-query'

import { DocumentTitle } from '@/components/document-title'
import { useKiosk } from '@/hooks/use-kiosk'
import { useLocalizeDocumentAttributes } from '@/hooks/use-localize-document-attributes'
import { dexfSettingsQueryOptions, initAnalyticsQueryOptions, translationsQueryOptions } from '@/query-options'
import { getLocaleFromPath } from '@/utils/base-path-params'
import type { SceneHistoryMap } from '@/utils/scene-history-map'

import styles from './__root.module.css'

const rootSearchSchema = z.object({
  brygga:
    import.meta.env.MODE !== 'production'
      ? z
          .object({
            catalog: z.coerce.string(),
            pkg: z.coerce.string(),
          })
          .optional()
      : z.undefined().optional(),
  dev: import.meta.env.MODE !== 'production' ? z.boolean().optional() : z.undefined().optional(),
  storeId: z.union([z.string(), z.number()]).transform(String).optional(),
  computerId: z.string().optional(),
  allowPrint: z.boolean().optional(),
  idleTime: z.number().optional(),
  origin: z.enum(['upptacka']).optional(),
  ipexGallery: z.boolean().optional(),
  hasPriceDisclaimerBeenShown: z.boolean().optional(),
})

export type RootSearch = z.infer<typeof rootSearchSchema>

type RouterContext = {
  queryClient: QueryClient
  sceneHistory: SceneHistoryMap
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  validateSearch: rootSearchSchema,
  search: {
    middlewares: [retainSearchParams(rootSearchSchema.keyof().options)],
  },
  beforeLoad: async ({ context, search }) => {
    await context.queryClient.ensureQueryData(translationsQueryOptions(getLocaleFromPath()))
    const dexfSettings = await context.queryClient.ensureQueryData(dexfSettingsQueryOptions(getLocaleFromPath()))

    const { storeId, computerId } = search
    const insightsApi = await context.queryClient.ensureQueryData(
      initAnalyticsQueryOptions(dexfSettings, computerId, storeId),
    )
    return { insightsApi }
  },
})

function RootLayout() {
  useLocalizeDocumentAttributes()
  const { dev: isDev } = Route.useSearch()
  const { kioskIntegration, platform } = useKiosk()
  const isKiosk = platform !== 'web'

  useEffect(() => {
    if (!isKiosk) return
    kioskIntegration.applicationLoaded()
    kioskIntegration.handleCountdownBanner()
  }, [isKiosk, kioskIntegration])

  const handlePointerDown = isKiosk
    ? () => {
        kioskIntegration.userInteraction()
      }
    : undefined

  return (
    <>
      <HeadContent />
      <DocumentTitle />
      <div className={styles.rootContainer} onPointerDown={handlePointerDown}>
        <Outlet />
        <Suspense fallback={null}>{isDev && <SofaDevtools />}</Suspense>
      </div>
    </>
  )
}
